import { useLazyQuery, useMutation } from '@apollo/client';
import { Chip, Icon, IconButton, TableColumnType, TableOrderByType, Tooltip } from '@elipssolution/harfang';
import { mdiDelete, mdiInformationOutline } from '@mdi/js';
import { useCallback, useMemo } from 'react';

import AddCustomerFileUserRelationForm from './AddCustomerFileUserRelationForm';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import SettingsTable from '../../../../components/SettingsTable';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { CustomerFileType } from '../../../../types/customerFile';
import { renderFullName } from '../../../../utils/renderFullName';
import { FETCH_INTERNAL_USERS_BY_CUSTOMER_FILE, FetchInternalUsersByCustomerFileType } from '../../../api/internalUser';
import { REMOVE_INTERNAL_USER_CUSTOMER_FILE_RELATION_BY_CUSTOMER_FILE_AND_USER } from '../../../api/internalUserCustomerFile';
import { InternalUserType } from '../../../types/user';

type CustomerFileProps = {
	customerFile: CustomerFileType;
};

const InternalCustomerFileUserRelation = ({ customerFile }: CustomerFileProps) => {
	const { id: customerFileId, name: customerFileName } = customerFile;

	const { push } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const [
		removeInternalUserCustomerFileRelation,
		{ called: isRemoveInternalUserCustomerFileRelationCalled, reset: resetRemoveInternalUserCustomerFileRelation },
	] = useMutation(REMOVE_INTERNAL_USER_CUSTOMER_FILE_RELATION_BY_CUSTOMER_FILE_AND_USER);

	const handleRemoveRelationButtonClick = useCallback(
		async (userId: InternalUserType['id']) => {
			const isRemovalConfirmed = await confirmAction({
				title: 'Êtes-vous sûr de vouloir supprimer la relation utilisateur / dossier client ?',
				confirmButtonDetails: {
					color: 'error',
					label: 'Supprimer',
				},
			});

			if (!isRemovalConfirmed) return null;

			return removeInternalUserCustomerFileRelation({
				variables: {
					customerFileId,
					userId,
				},
			});
		},
		[confirmAction, customerFileId, removeInternalUserCustomerFileRelation],
	);

	const columns: TableColumnType<InternalUserType>[] = useMemo(
		() => [
			{
				key: 'internalUser',
				field: 'lastName',
				render: (user) => renderFullName(user),
				title: "Nom de l'utilisateur",
				width: 200,
				sortable: true,
			},
			{
				field: 'email',
				key: 'email',
				render: ({ email }) => email,
				title: 'Email',
				width: 250,
				sortable: true,
			},
			{
				flexGrow: 0,
				key: 'hasUserPermissions',
				render: ({ hasUserPermissions }) =>
					hasUserPermissions && (
						<Tooltip content="Les droits de l'utilisateur ont été personnalisés">
							<Icon path={mdiInformationOutline} />
						</Tooltip>
					),
				width: 24,
			},
			{
				flexGrow: 0,
				key: 'group',
				render: ({ group: { isAdministrator, isDefault, name } }) => {
					if (isAdministrator) return <Chip label={name} color="error" />;

					return <Chip label={name} color={isDefault ? 'info' : 'default'} />;
				},
				width: 150,
			},
			{
				align: 'center',
				key: 'actions',
				flexGrow: 0,
				render: ({ id, group: { isAdministrator } }) =>
					!isAdministrator && (
						<IconButton onClick={() => handleRemoveRelationButtonClick(id)}>
							<Icon path={mdiDelete} />
						</IconButton>
					),
				width: 40,
			},
		],
		[handleRemoveRelationButtonClick],
	);

	const [fetchInternalUserCustomerFileByCustomerFile] = useLazyQuery<FetchInternalUsersByCustomerFileType>(
		FETCH_INTERNAL_USERS_BY_CUSTOMER_FILE,
	);

	const internalUserCustomerFileByCustomerFileDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<InternalUserType>,
		): Promise<{
			count: number;
			items: InternalUserType[];
		}> => {
			const { field, order } = orderBy || {};

			const { data, error } = await fetchInternalUserCustomerFileByCustomerFile({
				variables: {
					customerFileId,
					page: {
						limit,
						offset,
					},
					...(orderBy && { orderBy: { field, order } }),
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				internalUsersByCustomerFile: { count = 0, items = [] },
			} = data ?? {
				internalUsersByCustomerFile: {},
			};

			isRemoveInternalUserCustomerFileRelationCalled && resetRemoveInternalUserCustomerFileRelation();

			return { count, items };
		},
		[
			fetchInternalUserCustomerFileByCustomerFile,
			customerFileId,
			isRemoveInternalUserCustomerFileRelationCalled,
			resetRemoveInternalUserCustomerFileRelation,
		],
	);

	const handleAddButtonClick = useCallback(
		() => push(<AddCustomerFileUserRelationForm customerFile={customerFile} />),
		[customerFile, push],
	);

	return (
		<SettingsDialogPage title={customerFileName}>
			<SettingsTable
				addButtonLabel="Ajout d'utilisateurs"
				dataSource={internalUserCustomerFileByCustomerFileDataSource}
				onAddButtonClick={handleAddButtonClick}
				tableColumns={columns}
				title="Utilisateurs"
				height="100%"
			/>
		</SettingsDialogPage>
	);
};

export default InternalCustomerFileUserRelation;
